import React, { useEffect } from "react";
import styles from "./NoAccessComponents.module.scss";
import { ReactComponent as ForbiddenIcon } from "./forbidden.svg";
import classNames from "classnames";
import typography from "ui-components-rollup/src/components/typography.module.scss";
import icons from "ui-components-rollup/src/components/icons.module.scss";
import ExternalLink from "components/ExternalLink";
import externals from "./../../../utils/externals";
import buttons from "ui-components-rollup/src/components/buttons.module.scss";
import { TabHeader } from "ui-components-rollup/dist/Tabs";
import { ReactComponent as LockIcon } from "ui-components-rollup/src/images/monochrome-icons/ic-lock.svg";
import { useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { ReactComponent as ComingSoonIcon } from "./coming-soon.svg";
import { ReactComponent as UpgradeRequiredIcon } from "./upgrade-required.svg";
import { Button } from "ui-components-rollup/dist/Widgets";

export const Forbidden = () => {
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden}/>
        <div className={classNames(typography.h01, typography["text-blue"])}>Contact the administrator of this system to gain access</div>
        <p>Before you can use this connector, the administrator must add your user to the NetApp Account.</p>
        <ExternalLink trackCategory="NoAccessPages" href={externals.saasFrontend} newTab className={classNames(buttons.primary, buttons.large)}>Go to Cloud Manager SaaS</ExternalLink>
    </section>
};

export const NoGovCloudInSaas = () => {
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden}/>
        <div className={classNames(typography.h01, typography["text-blue"])}>Your account does not have access to this interface.</div>
        <p>Your Cloud Central account includes a Connector that’s located in a Government region.</p>
        <p>If you have any questions, reach out to us by using the chat feature below.</p>
        <ExternalLink trackCategory="NoAccessPages" href="https://docs.netapp.com/us-en/occm/reference_limitations.html" newTab includeArrow className={classNames(typography["text-blue"], icons.primary)}>Learn more about Gov Cloud
            Restrictions</ExternalLink>
    </section>
};

export const SaaSDisabled = () => {
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden}/>
        <div className={classNames(typography.h01, typography["text-blue"])}>Your account does not have access to this interface.</div>
        <p>Your Cloud Central account has been disabled for use with the SaaS interface.</p>
        <p>If you have any questions, reach out to us by using the chat feature below.</p>
    </section>
};

export const ServiceNeedsSaaSIsGov = ({ needHeader, currentService: { logo, service: { label } } }) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container}>
            <LockIcon className={styles['not-reachable']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>This service isn't supported with Government regions.</div>
            <p>Your Cloud Central account includes a Connector that’s located in a Government region.</p>
            <p>If you have any questions, reach out to us by using the chat feature below.</p>
            <ExternalLink trackCategory="NoAccessPages" href="https://docs.netapp.com/us-en/occm/reference_limitations.html" newTab includeArrow className={classNames(typography["text-blue"], typography["icon-blue"])}>Learn more about Gov
                Cloud Restrictions</ExternalLink>
        </section>
    </>
};

export const ServiceNeedsSaaSIsDisabled = ({ needHeader, currentService: { logo, service: { label } } }) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container}>
            <LockIcon className={styles['not-reachable']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>This service isn't supported when SaaS is disabled on your account.</div>
            <p>Your Cloud Central account has been disabled for use with the SaaS interface.</p>
            <p>If you have any questions, reach out to us by using the chat feature below.</p>
        </section>
    </>
};

export const NoContainerMessage = ({ needHeader, currentService: { logo, service: { label } } }) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container}>
            <LockIcon className={styles['not-reachable']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>We can’t access this service right now.</div>
            <p>Try again in a few minutes.</p>
            <p>Contact support if the service is still unavailable.</p>
        </section>
    </>
};

export const UpgradeRequired = ({ needHeader, currentService: { logo, service: { label } } }) => {
    const dispatch = useDispatch();
    const provider = useSelector(state => state.tenancy.selectedAgent?.provider)

    let type = "instance type"

    if (provider === "gcp") {
        type = "machine type";
    } else if (provider === "azure") {
        type = "VM type";
    } else if (provider === "on-prem") {
        type = "VM"
    }

    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container} style={{ paddingTop: 60 }}>
            <UpgradeRequiredIcon style={{ marginBottom: 13 }}/>
            <div className={classNames(typography.h01, typography.error, typography.b)}><span style={{ textTransform: "capitalize" }}>{type}</span> change is required.</div>
            <p style={{ maxWidth: 485, textAlign: "center" }}>You need to change the {type} for your Connector so you can enjoy new capabilities that you’re currently missing.</p>
            <Button style={{ width: 152, marginTop: 32 }} large onClick={() => {
                dispatch({
                    type: "CURRENT-CONNECTOR:OPEN-WEAK-CONNECTOR-DIALOG",
                    track: "from-no-access-page"
                })
            }}>Learn More</Button>
        </section>
    </>
};

export const NoLocalMessage = ({ needHeader, currentService: { logo, service: { label } } }) => {
    const location = useLocation();
    const selectedAccountId = useSelector(state => state.tenancy.selectedAccountId);
    const connectorId = useSelector(state => state.tenancy.selectedAgentId);
    const connectorIdSuffix = connectorId ? `&connectorId=${connectorId}` : "";
    const url = `${externals.saasFrontend}${location.pathname}?accountId=${selectedAccountId}${connectorIdSuffix}`;

    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container}>
            <LockIcon className={styles['not-reachable']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>This service is only accessible from Cloud Manager SaaS.</div>
            <p>Visit the Cloud Managaer SaaS UI to use this service.</p>
            <ExternalLink
                className={classNames(buttons.primary, buttons.large)}
                href={url}
                newTab
            > Go to Cloud Manager SaaS</ExternalLink>
        </section>
    </>
};

export const ComingSoonMessage = ({ needHeader, currentService: { logo, service: { label } } }) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container}>
            <ComingSoonIcon className={styles['coming-soon']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>We can’t access this service right now.</div>
            <p>This service is not yet supported with the cloud provider where the Connector is deployed.</p>
        </section>
    </>
};

export const NoPermissionMessage = ({ needHeader, currentService }) => {
    const { logo, service: { label } = {} } = currentService || {};
    const userRole = useSelector(state => state.tenancy.selectedAccount?.userRole)
    const availableRoles = useSelector(state => state.tenancy.availableRoles);
    const role = availableRoles[userRole];
    return <>
        {currentService && needHeader ? <TabHeader logo={logo} label={label}/> : ''}
        <section className={styles.container}>
            <LockIcon className={styles['not-reachable']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>Your user role ({role}) doesn’t allow you to access this service.</div>
        </section>
    </>
};

export const NeedSaaSRedirect = React.memo(({ needHeader, currentService: { logo, service: { label } } }) => {
    const location = useLocation();
    const selectedAccountId = useSelector(state => state.tenancy.selectedAccountId);
    const connectorId = useSelector(state => state.tenancy.selectedAgentId);

    const url = `${externals.saasFrontend}${location.pathname}?accountId=${selectedAccountId}&connectorId=${connectorId}`;

    useEffect(() => {
        window.open(url, "_blank")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        {needHeader && <TabHeader logo={logo} label={label}/>}
        <section className={styles.container}>
            <LockIcon className={styles['not-reachable']}/>
            <div className={classNames(typography.h01, typography["text-blue"])}>Service requires a secure connection.</div>
            <p>This service is only accessible from Cloud Manager SaaS or local UI served over HTTPS.</p>
            <ExternalLink
                className={classNames(buttons.primary, buttons.large)}
                href={url}
                newTab
            > Go to Cloud Manager SaaS</ExternalLink>
        </section>
    </>
});
